import React, { useState } from 'react';
import { UserPlus, Shield, Edit, Trash2, Mail } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';
import { toast } from 'sonner';

const mockAdmins = [
  {
    id: '1',
    name: 'John Admin',
    email: 'john@propfirmhouse.com',
    role: 'Super Admin',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=John',
    lastLogin: '2026-01-09T14:23:00Z',
    status: 'active',
  },
  {
    id: '2',
    name: 'Sarah Editor',
    email: 'sarah@propfirmhouse.com',
    role: 'Content Editor',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah',
    lastLogin: '2026-01-09T10:15:00Z',
    status: 'active',
  },
  {
    id: '3',
    name: 'Mike Moderator',
    email: 'mike@propfirmhouse.com',
    role: 'Moderator',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Mike',
    lastLogin: '2026-01-08T16:40:00Z',
    status: 'active',
  },
];

const permissions = {
  'Prop Firms': ['View', 'Create', 'Edit', 'Delete', 'Publish'],
  'Unlisted Firms': ['View', 'Create', 'Edit', 'Delete'],
  'Blog & Resources': ['View', 'Create', 'Edit', 'Delete', 'Publish'],
  'News': ['View', 'Create', 'Edit', 'Delete'],
  'Reviews': ['View', 'Approve/Reject', 'Reply', 'Delete'],
  'Verified Payouts': ['View', 'Verify', 'Reject', 'Delete'],
  'Users': ['View', 'Edit', 'Suspend', 'Delete'],
  'Loyalty Program': ['View', 'Edit Rules', 'Adjust Points'],
  'Leaderboard': ['View', 'Edit'],
  'Admin Accounts': ['View', 'Invite', 'Edit Roles', 'Delete'],
  'Site Settings': ['View', 'Edit'],
  'Redirects': ['View', 'Create', 'Edit', 'Delete'],
  'Announcements': ['View', 'Create', 'Edit', 'Delete'],
};

const defaultRoles = {
  'Super Admin': Object.keys(permissions).reduce((acc, module) => {
    acc[module] = permissions[module];
    return acc;
  }, {}),
  'Admin': Object.keys(permissions).reduce((acc, module) => {
    if (module !== 'Admin Accounts') {
      acc[module] = permissions[module];
    }
    return acc;
  }, {}),
  'Content Editor': {
    'Prop Firms': ['View', 'Create', 'Edit'],
    'Blog & Resources': ['View', 'Create', 'Edit', 'Publish'],
    'News': ['View', 'Create', 'Edit'],
    'Reviews': ['View'],
  },
  'Moderator': {
    'Reviews': ['View', 'Approve/Reject', 'Reply', 'Delete'],
    'Verified Payouts': ['View', 'Verify', 'Reject'],
    'Users': ['View', 'Suspend'],
  },
};

export const AdminAccountsPage = () => {
  const [admins, setAdmins] = useState(mockAdmins);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [deleteAdmin, setDeleteAdmin] = useState(null);
  const [editAdmin, setEditAdmin] = useState(null);
  const [editAdminRole, setEditAdminRole] = useState('');
  const [selectedRole, setSelectedRole] = useState('Content Editor');
  const [rolePermissions, setRolePermissions] = useState(defaultRoles[selectedRole]);

  const togglePermission = (module, permission) => {
    setRolePermissions(prev => {
      const modulePerms = prev[module] || [];
      const hasPermission = modulePerms.includes(permission);
      
      if (hasPermission) {
        return {
          ...prev,
          [module]: modulePerms.filter(p => p !== permission),
        };
      } else {
        return {
          ...prev,
          [module]: [...modulePerms, permission],
        };
      }
    });
  };

  return (
    <div>
      <PageHeader
        title="Admin Accounts"
        description="Manage admin users and role permissions"
        breadcrumbs={['Admin', 'System', 'Admin Accounts']}
      />

      <Tabs defaultValue="admins" className="space-y-6">
        <TabsList>
          <TabsTrigger value="admins">Admins</TabsTrigger>
          <TabsTrigger value="roles">Roles & Permissions</TabsTrigger>
        </TabsList>

        <TabsContent value="admins" className="space-y-6">
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Admin Users</CardTitle>
                <Button onClick={() => setInviteDialogOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite Admin
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Admin</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Last Login</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {admins.map((admin) => (
                      <TableRow key={admin.id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={admin.avatar} />
                              <AvatarFallback>{admin.name[0]}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{admin.name}</p>
                              <p className="text-xs text-slate-500">{admin.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={
                              admin.role === 'Super Admin'
                                ? 'bg-purple-100 text-purple-700'
                                : admin.role === 'Admin'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-slate-100 text-slate-700'
                            }
                          >
                            {admin.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">
                            {format(new Date(admin.lastLogin), 'MMM dd, yyyy HH:mm')}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`h-2 w-2 rounded-full ${admin.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            <span className="text-sm capitalize">{admin.status}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => {
                                setEditAdmin(admin);
                                setEditAdminRole(admin.role);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {admin.role !== 'Super Admin' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-600"
                                onClick={() => setDeleteAdmin(admin)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roles" className="space-y-6">
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Role Permissions</CardTitle>
                <Select value={selectedRole} onValueChange={(val) => { setSelectedRole(val); setRolePermissions(defaultRoles[val]); }}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Super Admin">Super Admin</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Content Editor">Content Editor</SelectItem>
                    <SelectItem value="Moderator">Moderator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {Object.entries(permissions).map(([module, perms]) => (
                  <div key={module} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="h-4 w-4 text-slate-600" />
                      <h3 className="font-semibold text-slate-900">{module}</h3>
                    </div>
                    <div className="grid grid-cols-5 gap-4">
                      {perms.map((permission) => {
                        const isChecked = rolePermissions[module]?.includes(permission) || false;
                        const isDisabled = selectedRole === 'Super Admin' || (selectedRole === 'Admin' && module === 'Admin Accounts');
                        
                        return (
                          <div key={permission} className="flex items-center space-x-2">
                            <Checkbox
                              id={`${module}-${permission}`}
                              checked={isChecked}
                              onCheckedChange={() => togglePermission(module, permission)}
                              disabled={isDisabled}
                            />
                            <Label
                              htmlFor={`${module}-${permission}`}
                              className={`text-sm ${isDisabled ? 'text-slate-400' : 'text-slate-700'}`}
                            >
                              {permission}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <Button className="mt-6" onClick={() => toast.success('Permissions saved')}>
                Save Permissions
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Name</Label>
              <Input placeholder="Enter name" className="mt-2" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" placeholder="admin@propfirmhouse.com" className="mt-2" />
            </div>
            <div>
              <Label>Role</Label>
              <Select defaultValue="admin">
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Content Editor</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={() => { toast.success('Invitation sent'); setInviteDialogOpen(false); }}>
              <Mail className="h-4 w-4 mr-2" />
              Send Invitation
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editAdmin} onOpenChange={() => setEditAdmin(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Admin Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Admin</Label>
              <p className="text-sm font-medium text-slate-900 mt-2">{editAdmin?.name}</p>
              <p className="text-xs text-slate-500">{editAdmin?.email}</p>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={editAdminRole} onValueChange={setEditAdminRole}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Super Admin">Super Admin</SelectItem>
                  <SelectItem value="Admin">Admin</SelectItem>
                  <SelectItem value="Content Editor">Content Editor</SelectItem>
                  <SelectItem value="Moderator">Moderator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              className="w-full"
              onClick={() => {
                setAdmins(admins.map(a => a.id === editAdmin.id ? { ...a, role: editAdminRole } : a));
                toast.success('Admin role updated');
                setEditAdmin(null);
              }}
            >
              Update Role
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteAdmin}
        onOpenChange={() => setDeleteAdmin(null)}
        title="Delete Admin"
        description={`Are you sure you want to delete ${deleteAdmin?.name}? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          setAdmins(admins.filter(a => a.id !== deleteAdmin.id));
          toast.success('Admin deleted');
          setDeleteAdmin(null);
        }}
      />
    </div>
  );
};
